import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Container,
  Paper,
  TextInput,
  PasswordInput,
  Button,
  Title,
  Text,
  Image,
  Stack,
  Alert,
  Anchor,
  Divider,
} from "@mantine/core";
import { IconAlertCircle, IconFingerprint } from "@tabler/icons-react";
import { browserSupportsWebAuthn } from "@simplewebauthn/browser";
import { useAuthStore } from "../store";
import { setAuthToken } from "../lib";
import { authService } from "../services";
import { usePasskeyLogin } from "../hooks/usePasskeys";

const GCP_LOGO_URL =
  "https://i0.wp.com/gulfcoastpath.com/wp-content/uploads/2021/02/cropped-GCP-Logo-v1.png?fit=1628%2C1071&ssl=1";

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const passkeyLogin = usePasskeyLogin();

  const webAuthnSupported = browserSupportsWebAuthn();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await authService.login({ username, password });

      setAuthToken(response.token);
      login(response.token, {
        id: response.user.id,
        username: response.user.username,
        email: response.user.email,
        fullName: response.user.fullName,
        roleId: response.user.roleId || "",
        roleName: response.user.roleName,
        groups: response.user.groups || [],
        groupId: response.user.groupId || undefined,
        groupName: response.user.groupName,
        facilityId: response.user.facilityId || undefined,
        facilityName: response.user.facilityName,
        isActive: response.user.isActive,
        isRemote: response.user.isRemote,
      });

      if (response.mustChangePassword) {
        navigate("/change-password");
        return;
      }

      const pathologistGuardEnabled =
        process.env.REACT_APP_ENABLE_PATHOLOGIST_GUARD !== "false";
      navigate(
        response.user.roleName === "Pathologist" && pathologistGuardEnabled
          ? "/pathologist-review"
          : "/dashboard"
      );
    } catch (err: unknown) {
      const errorMessage =
        err && typeof err === "object" && "message" in err
          ? (err as { message: string }).message
          : "Invalid credentials";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeyLogin = () => {
    setError("");
    passkeyLogin.mutate(username || undefined);
  };

  return (
    <Container size={420} my={40}>
      <Stack align="center" mb="xl">
        <Image src={GCP_LOGO_URL} alt="GCP" h={80} fit="contain" />
        <Title order={2} c="gcpBlue">
          GCP LIS
        </Title>
        <Text c="dimmed" size="sm">
          Laboratory Information System
        </Text>
      </Stack>

      <Paper withBorder shadow="md" p={30} radius="md">
        <Stack>
          {error && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              variant="light"
            >
              {error}
            </Alert>
          )}

          {webAuthnSupported && (
            <>
              <Button
                fullWidth
                variant="light"
                leftSection={<IconFingerprint size={18} />}
                onClick={handlePasskeyLogin}
                loading={passkeyLogin.isPending}
              >
                Sign in with Passkey
              </Button>

              <Divider
                label="or sign in with password"
                labelPosition="center"
              />
            </>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit(e).catch(() => {});
            }}
          >
            <Stack>
              <TextInput
                label="Username or Email"
                placeholder="Enter your username or email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />

              <PasswordInput
                label="Password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />

              <Button type="submit" fullWidth loading={loading}>
                Sign in
              </Button>
            </Stack>
          </form>

          <Anchor component={Link} to="/forgot-password" size="sm" ta="center">
            Forgot your password?
          </Anchor>
        </Stack>
      </Paper>

      <Text c="dimmed" size="sm" ta="center" mt="md">
        Are you a physician?{" "}
        <Anchor component={Link} to="/select-portal" size="sm">
          Access the Physician Portal
        </Anchor>
      </Text>
    </Container>
  );
}
